import dayjs from "dayjs";

import { formatCurrency } from "@/helpers/currency-helpers";
import ICustomerSalesProduct from "@/interfaces/customer-sales-product.interfaces";
import { PlansTableMessages } from "@/messages/customer/plans-table.messages";
import { CustomerSalesProductStatus } from "@/types/customer-sales-product.types";
import { FixedMonthlyFeeType } from "@/types/fixed-monthly-fee-type.types";

type UsePlanDataReturn = Partial<ICustomerSalesProduct> & {
  formattedStartDate: string;
  formattedEndDate: string;
  formattedNextPriceUpdate: string;
  formattedNextMonthlyPayment: string;
  formattedDueDay: string;
  formattedPricePerEvent: string;
  formattedEventsIncludedMonthlyFee: number;
  formattedFeePerSalesConvertion: string;
  formattedFixedFeePerSalesConvertion: string;
  formattedFixedMonthlyFee: string;
  fixedMonthlyFeeTypeLabel: string;
  statusLabel: string;
};

type UsePlanDataProps = {
  plan: ICustomerSalesProduct | null;
  messages: PlansTableMessages;
};

function usePlanData({ plan, messages }: UsePlanDataProps): UsePlanDataReturn {
  const formattedStartDate = plan?.startDate ? dayjs(plan.startDate).format("DD/MM/YYYY") : "-";

  const formattedEndDate = plan?.endDate ? dayjs(plan.endDate).format("DD/MM/YYYY") : "-";

  const formattedNextPriceUpdate = plan?.nextPriceUpdate
    ? dayjs(plan.nextPriceUpdate).format("DD/MM/YYYY")
    : "-";

  const formattedNextMonthlyPayment = plan?.nextMonthlyPayment
    ? dayjs(plan.nextMonthlyPayment).format("DD/MM/YYYY")
    : "-";

  const formattedDueDay = plan?.dueDay ? `Dia ${plan.dueDay}` : "-";

  const formattedPricePerEvent = (plan?.pricePerEvent || 0).toFixed(2).replace(".", ",");

  const formattedEventsIncludedMonthlyFee = plan?.eventsIncludedMonthlyFee || 0;

  const formattedFeePerSalesConvertion = (plan?.feePerSalesConvertion || 0)
    .toFixed(2)
    .replace(".", ",");

  const formattedFixedFeePerSalesConvertion = formatCurrency(
    plan?.fixedFeePerSalesConvertion || 0,
    "pt-BR",
    "BRL"
  );

  const formattedFixedMonthlyFee = formatCurrency(plan?.fixedMonthlyFee || 0);

  const getFixedMonthlyFeeTypeLabel = (feeType?: FixedMonthlyFeeType): string => {
    switch (feeType) {
      case FixedMonthlyFeeType.Integral:
        return messages.integralFeeTypeLabel;
      case FixedMonthlyFeeType.MinimumConsumption:
        return messages.minimumConsumptionFeeTypeLabel;
      default:
        return "-";
    }
  };

  const fixedMonthlyFeeTypeLabel = getFixedMonthlyFeeTypeLabel(plan?.fixedMonthlyFeeType);

  const getStatusLabel = (status: CustomerSalesProductStatus): string => {
    switch (status) {
      case CustomerSalesProductStatus.Active:
        return messages.activeStatusMessage;
      case CustomerSalesProductStatus.Inactive:
        return messages.inactiveStatusMessage;
      case CustomerSalesProductStatus.HoldDuePaymentIssue:
        return messages.holdStatusMessage;
      case CustomerSalesProductStatus.Cancelled:
        return messages.cancelledStatusMessage;
      default:
        return "-";
    }
  };

  const statusLabel = plan ? getStatusLabel(plan.status) : "-";

  return {
    ...plan,
    formattedStartDate,
    formattedEndDate,
    formattedNextPriceUpdate,
    formattedNextMonthlyPayment,
    formattedDueDay,
    formattedPricePerEvent,
    formattedEventsIncludedMonthlyFee,
    formattedFeePerSalesConvertion,
    formattedFixedFeePerSalesConvertion,
    formattedFixedMonthlyFee,
    fixedMonthlyFeeTypeLabel,
    statusLabel,
  };
}

export default usePlanData;
